<script>
import InputControl from '@mobile/components/Form/InputControl.vue';
import Checkbox from '@mobile/components/Checkbox.vue';
import InputControlDate from '@/components/Form/InputControlDate.vue';
import { ISSUE_PRIORITY, ISSUE_STATUS } from '@/constants';
import CloseButton from '@/components/Modals/CloseButton.vue';
import DropdownModal from '@/views/ConductInspection/Modals/DropdownModal.vue';

export default {
    name: 'IssuesFilterModal',

    components: {
        CloseButton,
        DropdownModal,
        Checkbox,
        InputControl,
        InputControlDate,
    },

    props: {
        filters: {
            type: Object,
            required: true,
        },
        filterOptions: {
            type: Object,
            required: true,
        },
        status: {
            type: Number,
            required: false,
            default: ISSUE_STATUS.OPEN,
            validator(status) {
                return Object.entries(ISSUE_STATUS).find(
                    ([, value]) => value === status,
                );
            },
        },
    },

    data() {
        return {
            showAssigneeModal: false,
            form: {
                assignees: null,
                priority: null,
                status: null,
                sortBy: null,
                deadline: null,
                noAssignee: null,
                from: null,
                to: null,
            },
        };
    },

    computed: {
        priorities() {
            return {
                [ISSUE_PRIORITY.MEDIUM]: this.$t('Normal'),
                [ISSUE_PRIORITY.HIGH]: this.$t('High'),
            };
        },
        statuses() {
            return {
                [ISSUE_STATUS.OPEN]: this.$t('ToDo'),
                [ISSUE_STATUS.IN_PROGRESS]: this.$t('Started'),
            };
        },
        deadlines() {
            return {
                noDeadline: this.$t('No deadline'),
                overdue: this.$t('Overdue'),
                due: this.$t('Due'),
            };
        },
        sortBy() {
            return {
                priority: this.$t('High priority first'),
                created_at: this.$t('Newest issue first'),
            };
        },
        assignees() {
            const encodeAssignees = (assignees) => Object.keys(assignees).map((key) => ({
                uuid: key,
                title: assignees[key],
            }));

            return encodeAssignees(this.filterOptions.assignees);
        },
        selectedAssignees() {
            return this.form.assignees.map(String);
        },
        isStatusSolved() {
            return this.status === ISSUE_STATUS.SOLVED;
        },
    },

    created() {
        this.form = {
            ...this.form,
            ...this.filters,
        };
    },

    methods: {
        handleClose() {
            this.$emit('close');
        },

        filterButtonClicked() {
            this.$emit('update', this.form);
            this.handleClose();
        },

        selectAssigneeClicked() {
            if (this.form.noAssignee) return;
            this.showAssigneeModal = true;
        },

        assigneeModalSaved(assignees) {
            this.form.assignees = assignees.map(Number);
            this.assigneeModalClosed();
        },

        assigneeModalClosed() {
            this.showAssigneeModal = false;
        },
    },
};
</script>

<template>
  <modal @modal-close="handleClose">
    <div class="modal-wrapper">
      <CloseButton @click="handleClose" />

        <div class="modal-content">
            <div
                v-if="!isStatusSolved"
                class="mb-6 mt-3 flex items-center"
                @click="form.noAssignee = !form.noAssignee">
                    <Checkbox :checked="form.noAssignee" />
                    <label class="flex-1 ml-3 text-80">
                        {{ $t('No assignee') }}
                    </label>
            </div>

            <InputControl :label="$t('Assignee')">
                <select
                v-model="form.assignees"
                class="w-full form-input form-select form-control form-input-bordered"
                :class="{'empty': form.assignees.length === 0}"
                >
                <option :value="[]">{{ $t('Select assignee') }}</option>
                <option v-for="assignee in assignees" :key="assignee.uuid" :value="[assignee.uuid]">{{ assignee.title}}</option>
                </select>
            </InputControl>

            <InputControl v-if="!isStatusSolved" :label="$t('Deadline')">
                <select
                v-model="form.deadline"
                class="w-full form-select"
                :class="{'empty': form.deadline === null}"
                >
                <option :value="null">{{ $t('Select deadline') }}</option>
                <option v-for="(name, value) in deadlines" :key="value" :value="value">{{ name }}</option>
                </select>
            </InputControl>

            <InputControl :label="$t('Priority')">
                <select
                v-model="form.priority"
                class="w-full form-select"
                :class="{'empty': form.priority === null}"
                >
                <option :value="null">{{ $t('Select priority') }}</option>
                <option
                    v-for="(name, value) in priorities"
                    :key="value"
                    :value="parseInt(value)"
                >{{ name }}</option>
                </select>
            </InputControl>

            <InputControl v-if="!isStatusSolved" :label="$t('Status')">
                <select
                v-model="form.status"
                class="w-full form-select"
                :class="{'empty': form.status === null}"
                >
                <option :value="null">{{ $t('Select status') }}</option>
                <option v-for="(name, value) in statuses" :key="value" :value="parseInt(value)">{{ name }}</option>
                </select>
            </InputControl>

            <InputControl v-if="!isStatusSolved" :label="$t('Sort by')">
                <select
                v-model="form.sortBy"
                class="w-full form-select"
                :class="{'empty': form.sortBy === null}"
                >
                <option :value="null">{{ $t('Sort by') }}</option>
                <option v-for="(name, value) in sortBy" :key="value" :value="value">{{ name }}</option>
                </select>
            </InputControl>

            <InputControl v-if="isStatusSolved" :label="$t('From (date of resolution)')">
                <InputControlDate
                placeholder="Select date"
                v-model="form.from"
                :input-class="{
                    'empty': !form.from,
                    'pb-2 form-control': true,
                }"
                wrap-size
                />
            </InputControl>

            <InputControl v-if="isStatusSolved" :label="$t('To (date of resolution)')">
                <InputControlDate
                placeholder="Select date"
                v-model="form.to"
                :input-class="{
                    'empty': !form.to,
                    'pb-2 form-control': true,
                }"
                wrap-size
                />
            </InputControl>
        </div>
        <div class="modal-actions-outer-wrapper">
            <div class="modal-actions-inner-wrapper">

                <button
                type="button"
                class="w-full btn btn-default btn-primary w-full medium:w-auto mt-4 medium:mt-0"
                @click.prevent="filterButtonClicked"
                >{{ $t('Filter') }}</button>
            </div>
        </div>

    <portal to="modals">
      <transition name="fade">
        <DropdownModal
          v-if="showAssigneeModal"
          :responses="assignees"
          :response="selectedAssignees"
          :multiple="true"
          @save="assigneeModalSaved"
          @close="assigneeModalClosed"
        />
      </transition>
    </portal>
    </div>
  </modal>
</template>
